(function () {
  'use strict';

  // Prevent double-binding if the script gets included twice
  if (window.__JM_CART_BOUND__) return;
  window.__JM_CART_BOUND__ = true;

  /* ========= Small debounce helper ========= */
  function debounce(fn, wait) {
    let timer = null;
    let pending = [];
    return function (...args) {
      const context = this;
      clearTimeout(timer);
      return new Promise((resolve, reject) => {
        pending.push({ resolve, reject });
        timer = setTimeout(() => {
          const waiters = pending;
          pending = [];
          Promise.resolve(fn.apply(context, args)).then(
            (value) => waiters.forEach((waiter) => waiter.resolve(value)),
            (error) => waiters.forEach((waiter) => waiter.reject(error))
          );
        }, wait);
      });
    };
  }

  /* ========= Toast ========= */
  const toastEl = document.getElementById('jm-cart-toast');
  let toastTimer = null;

  function jmToast(html, ms = 3500) {
    if (!toastEl) return;
    toastEl.innerHTML = html;
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      toastEl.classList.remove('is-visible');
    }, ms);
  }

  /* ========= Drawer + Elements ========= */
  const drawer = document.getElementById('jm-cart-drawer');
  const bodyEl = document.getElementById('jm-cart-body');
  let lastDrawerTrigger = null;

  function isAvailableFocusTarget(element) {
    if (!element || !element.isConnected || element.matches(':disabled, [aria-disabled="true"]')) return false;
    if (element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
    const style = getComputedStyle(element);
    return element.getClientRects().length > 0 && style.visibility !== 'hidden' && style.visibility !== 'collapse';
  }

  function drawerFocusTargets() {
    const panel = drawer && drawer.querySelector('.jm-cart-panel');
    if (!panel) return [];
    return Array.from(panel.querySelectorAll('a[href], button, input:not([type="hidden"]), select, textarea, [tabindex], [contenteditable="true"]'))
      .filter(element => element.tabIndex >= 0 && isAvailableFocusTarget(element));
  }

  function focusDrawerStart() {
    const targets = drawerFocusTargets();
    const target = targets.find(element => element.matches('.jm-cart-close')) || targets[0];
    if (target) {
      target.focus({ preventScroll: true });
    } else {
      const panel = drawer && drawer.querySelector('.jm-cart-panel');
      if (panel) {
        panel.setAttribute('tabindex', '-1');
        panel.focus({ preventScroll: true });
      }
    }
  }

  function syncDrawerA11y(isOpen) {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
    if (isOpen) {
      drawer.removeAttribute('inert');
    } else {
      drawer.setAttribute('inert', '');
    }
  }

  syncDrawerA11y(false);

  let cartGeneration = 0;
  let acceptedGeneration = 0;
  let acceptedCart = null;
  let renderedGeneration = 0;
  let inFlightRead = null;
  let hasRenderedCart = false;
  let addQueuedOrInFlight = false;

  function setCartBusy(isBusy) {
    if (bodyEl) bodyEl.setAttribute('aria-busy', isBusy ? 'true' : 'false');
  }

  function showInitialLoading() {
    if (!bodyEl || hasRenderedCart) return;
    bodyEl.innerHTML = '<p role="status">Loading cart…</p>';
  }

  function showCartError() {
    if (hasRenderedCart) {
      jmToast('Could not refresh cart. <a href="/cart">View cart</a>', 5000);
      return;
    }
    if (bodyEl) bodyEl.innerHTML = '<p>Could not load cart. <a href="/cart">View your cart</a>.</p>';
  }

  function openDrawerShell() {
    if (!drawer) return;
    const wasOpen = drawer.classList.contains('is-open');
    const active = document.activeElement;
    if (!wasOpen && active && active !== document.body && !drawer.contains(active)) {
      lastDrawerTrigger = active;
    }
    drawer.classList.add('is-open');
    syncDrawerA11y(true);
    document.documentElement.style.overflow = 'hidden';
    if (!wasOpen || !drawer.contains(document.activeElement)) focusDrawerStart();
  }

  function jmRefreshCart(options = {}) {
    const shouldRender = options.render !== false;
    const shouldOpen = options.open === true;
    const force = options.force === true;

    if (shouldOpen) openDrawerShell();

    if (shouldRender) {
      showInitialLoading();
      setCartBusy(true);
    }

    let generation;
    let readPromise;
    if (!force && inFlightRead) {
      generation = inFlightRead.generation;
      readPromise = inFlightRead.promise;
    } else {
      cartGeneration += 1;
      generation = cartGeneration;
      readPromise = jmFetchCart(generation);
      inFlightRead = { generation: generation, promise: readPromise };
      readPromise.finally(function () {
        if (inFlightRead && inFlightRead.promise === readPromise) inFlightRead = null;
      }).catch(function () {});
    }

    let terminalGeneration = generation;

    function acceptResult(result) {
      terminalGeneration = result.generation;
      if (result.generation !== cartGeneration || result.generation < acceptedGeneration) {
        const staleError = new Error('Stale cart response ignored');
        staleError.stale = true;
        staleError.generation = result.generation;
        throw staleError;
      }
      acceptedGeneration = result.generation;
      acceptedCart = result.cart;
      if (shouldRender && result.generation > renderedGeneration) {
        jmRenderDrawer(result.cart);
        renderedGeneration = result.generation;
        hasRenderedCart = true;
      }
      if (shouldRender) setCartBusy(false);
      return result.cart;
    }

    function followCurrent(supersededGeneration) {
      if (inFlightRead && inFlightRead.generation > supersededGeneration) {
        const followedGeneration = inFlightRead.generation;
        terminalGeneration = followedGeneration;
        return inFlightRead.promise.then(acceptResult).catch(function (error) {
          if (error.stale || cartGeneration > followedGeneration) {
            return followCurrent(followedGeneration);
          }
          throw error;
        });
      }
      if (acceptedCart && acceptedGeneration === cartGeneration) {
        return Promise.resolve({
          cart: acceptedCart,
          generation: acceptedGeneration
        }).then(acceptResult);
      }
      terminalGeneration = cartGeneration;
      return Promise.reject(new Error('Current cart response unavailable'));
    }

    const currentResult = readPromise.then(acceptResult).catch(function (error) {
      if (error.stale || cartGeneration > generation) {
        return followCurrent(generation);
      }
      throw error;
    });

    return currentResult.catch(function (error) {
      if (!error.stale) {
        console.error('[JM Cart] refresh error:', error);
        if (shouldRender) showCartError();
      }
      throw error;
    }).finally(function () {
      if (shouldRender && terminalGeneration === cartGeneration) setCartBusy(false);
    });
  }

  function jmOpenDrawer(options = {}) {
    openDrawerShell();
    if (options.refresh === false || addQueuedOrInFlight) {
      showInitialLoading();
      if (!hasRenderedCart) setCartBusy(true);
      return Promise.resolve(null);
    }
    // The lower-level refresh rejects; drawer open stays safe for legacy no-catch callers.
    return jmRefreshCart({ render: true }).catch(function () { return null; });
  }

  function jmCloseDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    syncDrawerA11y(false);
    document.documentElement.style.overflow = '';

    if (isAvailableFocusTarget(lastDrawerTrigger) && typeof lastDrawerTrigger.focus === 'function') {
      lastDrawerTrigger.focus({ preventScroll: true });
    }
  }

  function bindDrawerEvents() {
    if (!drawer) return;

    // Close on overlay click or [data-close]
    drawer.addEventListener('click', (e) => {
      if (e.target && e.target.hasAttribute('data-close')) jmCloseDrawer();
    });

    // Handle keys at the drawer root before downstream app keyboard guards.
    drawer.addEventListener('keydown', (e) => {
      if (!drawer.classList.contains('is-open')) return;
      if (e.key === 'Escape') {
        jmCloseDrawer();
        return;
      }
      if (e.key !== 'Tab') return;
      const targets = drawerFocusTargets();
      const first = targets[0];
      const last = targets[targets.length - 1];
      const active = document.activeElement;
      if (!first) {
        e.preventDefault();
        focusDrawerStart();
      } else if (!targets.includes(active) || (e.shiftKey ? active === first : active === last)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus({ preventScroll: true });
      }
    });

    // Make any ghost “View Cart & Checkout” links open the drawer
    // Matches: <a class="jm-btn-ghost" data-open-drawer> OR <a class="jm-btn-ghost" href="/cart">
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a');
      if (!a) return;
      if (a.matches('a.jm-btn-ghost[data-open-drawer], a.jm-btn-ghost[href="/cart"]')) {
        e.preventDefault();
        jmOpenDrawer();
      }
    });
  }
  bindDrawerEvents();

  /* ========= Cart helpers ========= */
  function jmFetchCart(generation) {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    // Bound cart reads so the drawer cannot remain in a loading state indefinitely.
    const timeoutId = setTimeout(function () {
      if (controller) controller.abort();
    }, 10000);

    return fetch('/cart.js', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' },
      cache: 'no-store',
      signal: controller ? controller.signal : undefined
    }).then(function (res) {
      if (!res.ok) throw new Error('Cart fetch failed');
      return res.json();
    }).then(function (cart) {
      return { cart: cart, generation: generation };
    }).finally(function () {
      clearTimeout(timeoutId);
    });
  }

  function money(cents) {
    const cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    try {
      return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: cur });
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  function jmItemUrl(it) {
    // Shopify cart.js often includes a product/variant URL already
    let url = it.url || '';

    // Fallback: build from handle
    if (!url && it.handle) url = `/products/${it.handle}`;

    // In /cart.js, "id" is usually the VARIANT id
    const variantId = it.variant_id || it.id;

    // Ensure variant is selected on the PDP
    if (url && variantId && !String(url).includes('variant=')) {
      url += (url.includes('?') ? '&' : '?') + `variant=${variantId}`;
    }

    return url || '/cart';
  }

  function jmRenderDrawer(cart) {
    if (!bodyEl) return;

    if (!cart.items || !cart.items.length) {
      bodyEl.innerHTML = `<p>Your cart is empty.</p>`;
    } else {
      const itemsHTML = cart.items.map((it) => {
        const href = jmItemUrl(it);

        // Try to render a small image if possible
        const img = (it.image || '').replace(
          /(\.jpg|\.jpeg|\.png)(\?.*)?$/i,
          (m, ext, q) => `_100x${ext}${q || ''}`
        );

        // Clean variant title (avoid "Default Title" showing)
        const variantTitle =
          it.variant_title && it.variant_title !== 'Default Title'
            ? it.variant_title
            : '';

        // Accessibility: give the link a real label
        const aria = `View ${it.product_title || 'product'}`;

        // Entire row clickable (enterprise UX)
        return `
          <a href="${href}"
             class="jm-cart-item-link"
             aria-label="${aria}"
             style="display:flex; gap:10px; padding:10px 0; border-bottom:1px solid #eee; color:inherit; text-decoration:none;">
            ${img ? `<img src="${img}" alt="" style="width:60px;height:60px;object-fit:cover;border-radius:8px;">` : ''}
            <div style="flex:1;">
              <div style="font-weight:700;margin-bottom:4px;">${it.product_title}</div>
              <div style="font-size:13px;opacity:.8;">${variantTitle}</div>
              <div style="font-size:13px;margin-top:4px;">Qty: ${it.quantity}</div>
            </div>
            <div style="font-weight:700;">${money(it.final_line_price)}</div>
          </a>
        `;
      }).join('');

      bodyEl.innerHTML = itemsHTML;
    }

    // Subtotal pill above the checkout button
    const footer = drawer?.querySelector('.jm-cart-footer');
    if (footer) {
      const old = footer.querySelector('.jm-subtotal-row');
      if (old) old.remove();

      const pill = document.createElement('div');
      pill.className = 'jm-subtotal-row';
      pill.innerHTML = `<span class="label">Subtotal</span><span class="amount">${money(cart.items_subtotal_price)}</span>`;

      const btn = footer.querySelector('.jm-cart-checkout-btn');
      footer.insertBefore(pill, btn || null);

      // --- Reassurance line just beneath subtotal (drawer only) ---
      // Remove any previous instance (prevents duplicates on qty updates)
      const existingAssurance = footer.querySelector('.jm-cart-assurance, .jm-cart-trust');
      if (existingAssurance) {
        footer.querySelectorAll('.jm-cart-assurance, .jm-cart-trust').forEach(n => n.remove());
      }

      const assuranceHTML = `
        <div class="jm-cart-assurance" role="note" aria-label="Shipping and handling info">
          <div><strong>Fast handling</strong> from <strong>Canton, MI, USA</strong></div>
          <div><strong>USPS Priority Mail</strong> 2–3 days <span class="jm-est">(estimates; not guaranteed)</span></div>
        </div>
        <p class="jm-cart-trust">
          <a href="https://www.trustpilot.com/review/jmsupplies.store" target="_blank" rel="noopener noreferrer">
            See our reviews on Trustpilot
          </a>
        </p>
        <!-- Optional eBay proof line -->
        <p class="jm-cart-trust" aria-label="eBay review proof">
          <a href="https://www.ebay.com/fdbk/feedback_profile/jcwent" target="_blank" rel="noopener noreferrer">
            99%+ positive over ~14,000 orders (eBay)
          </a>
        </p>
      `;

      // Insert right after the subtotal pill, before the checkout button
      pill.insertAdjacentHTML('afterend', assuranceHTML);
    }

    // --- Update PayPal "Pay Later" message in the drawer, if present ---
    try {
      // Only look inside the drawer so we don't touch the PDP trust card PayPal block
      const ppNode = drawer?.querySelector('[data-pp-message]');
      if (ppNode) {
        const amt = (cart.items_subtotal_price / 100).toFixed(2);
        ppNode.setAttribute('data-pp-amount', amt);

        if (window.paypal && window.paypal.Messages) {
          // Clear previous render to avoid duplication
          ppNode.innerHTML = '';
          window.paypal.Messages({ amount: amt }).render(ppNode);
        }
      }
    } catch (e) {
      console.warn('[JM Cart] PayPal message refresh error:', e);
    }

    // A refreshed body/footer can remove the focused link; keep focus in the open dialog.
    if (drawer && drawer.classList.contains('is-open') && !drawer.contains(document.activeElement)) {
      focusDrawerStart();
    }
  }

  /* ========= Add to cart (robust with clear errors) ========= */
  let addInFlight = false;

  async function extractError(res) {
    // Try JSON error shape first, then text
    try {
      const data = await res.clone().json();
      return data?.description || data?.message || data?.error || JSON.stringify(data);
    } catch {
      try {
        return await res.text();
      } catch {
        return 'Add failed';
      }
    }
  }

  async function addViaArray(items) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items })
    });
    if (!res.ok) {
      const errText = await extractError(res);
      const err = new Error(errText || 'Add failed');
      err.status = res.status;
      throw err;
    }
    return res.json();
  }

  async function addSequential(items) {
    for (const it of items) {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ id: it.id, quantity: it.quantity || 1 })
      });
      if (!res.ok) {
        const errText = await extractError(res);
        const err = new Error(errText || 'Add failed');
        err.status = res.status;
        throw err;
      }
      // No need to read body here
    }
  }

  async function _jmAddItems(items, { openDrawer = true } = {}) {
    if (addInFlight) return false; // guard against rapid re-clicks
    addInFlight = true;
    addQueuedOrInFlight = true;

    try {
      // Try multi-add first. If endpoint doesn't exist (404), fall back.
      try {
        await addViaArray(items);
      } catch (err) {
        if (err && err.status === 404) {
          await addSequential(items);
        } else {
          throw err;
        }
      }

      const cart = await jmRefreshCart({ force: true, render: true, open: openDrawer });

      const label = items.length > 1 ? 'Items added to cart.' : 'Added to cart.';
      jmToast(`${label} <a href="/cart">View cart</a>`, 4000);

      return cart;
    } catch (err) {
      console.error('[JM Cart] Add error:', err);
      if (drawer && drawer.classList.contains('is-open')) {
        setCartBusy(false);
        showCartError();
      }
      const msg = (err && err.message) ? err.message : 'Could not add to cart.';
      jmToast(`${msg} <a href="/cart">Open cart</a>`, 5000);
      return false;
    } finally {
      addInFlight = false;
      addQueuedOrInFlight = false;
    }
  }

  // Debounced public function; mark queued work synchronously for legacy inline open calls.
  const debouncedAddItems = debounce(_jmAddItems, 150);
  function jmAddItems(items, options) {
    addQueuedOrInFlight = true;
    return debouncedAddItems(items, options);
  }

  /* ========= Expose for Liquid buttons ========= */
  window.jmAddItems = jmAddItems;
  window.jmOpenCartDrawer = jmOpenDrawer;
  window.jmCloseCartDrawer = jmCloseDrawer;
  window.jmRefreshCart = jmRefreshCart;

  /* ========= Sync drawer after theme Ajax /cart/add ========= */
  if (window.jQuery) {
    try {
      jQuery(document).ajaxComplete(function (event, xhr, settings) {
        if (!settings || !settings.url) return;

        // Watch for any theme Ajax calls that add to cart
        var url = settings.url;
        if (url.indexOf('/cart/add') !== -1 || url.indexOf('/cart/add.js') !== -1) {
          if (!xhr || xhr.status < 200 || xhr.status >= 300) return;
          // Small delay to make sure Shopify has persisted the cart
          setTimeout(function () {
            if (typeof jmRefreshCart === 'function') {
              jmRefreshCart({ force: true, render: true, open: true }).catch(function () {});
            }
          }, 80);
        }
      });
    } catch (e) {
      console.warn('[JM Cart] ajaxComplete hook failed:', e);
    }
  }

})();
